import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminVolunteersQuery } from "@/lib/queries";
import { adminCreateVolunteer, adminSetVolunteerStatus } from "@/lib/admin.functions";
import { US_STATES } from "@/lib/us-states";
import { VolunteerDetailSheet } from "@/components/app/volunteer-detail-sheet";
import { reportHandledSync } from "@/lib/observability";



export const Route = createFileRoute("/_authenticated/t1dwpanel/volunteers")({
  head: () => ({
    meta: [
      { title: "Volunteers | Warrior Volunteer" },
      { name: "description", content: "Volunteers in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Volunteers | Warrior Volunteer" },
      { property: "og:description", content: "Volunteers in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VolunteersPage,
});

function VolunteersPage() {
  const { data: volunteers = [] } = useQuery(adminVolunteersQuery);
  const setStatus = useServerFn(adminSetVolunteerStatus);
  const createVolunteer = useServerFn(adminCreateVolunteer);
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openVolunteerId, setOpenVolunteerId] = useState<string | null>(null);

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      const result = await createVolunteer({
        data: {
          full_name: String(form.get("full_name") ?? ""),
          email: String(form.get("email") ?? ""),
          phone: String(form.get("phone") ?? ""),
          city: String(form.get("city") ?? ""),
          state: String(form.get("state") ?? ""),
          t1d_connection: String(form.get("t1d_connection") ?? ""),
          skills: String(form.get("skills") ?? ""),
          volunteer_track: String(form.get("volunteer_track") ?? "remote"),
          invite: form.get("invite") === "on",
        },
      });
      toast.success(
        result.existing
          ? "That person already had an account, so their volunteer profile was updated."
          : "Volunteer created. They can sign in with the invite email or a password reset.",
      );
      formEl.reset();
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "volunteers"] });
    } catch (error) {
      reportHandledSync(error, { feature: "admin", action: "handleCreate" });
      toast.error(error instanceof Error ? error.message : "Could not create the volunteer.");
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const header = [
      "Name",
      "Email",
      "Phone",
      "City",
      "State",
      "Shirt size",
      "Last event",
      "Last event date",
      "Status",
      "Joined",
    ];
    const rows = volunteers.map((v) => [
      v.full_name,
      v.email,
      v.phone ?? "",
      v.city ?? "",
      v.state ?? "",
      v.shirt_size ?? "",
      v.last_event?.title ?? "",
      v.last_event?.eventDate ?? "",
      v.status,
      new Date(v.joined_at).toISOString().slice(0, 10),
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "warrior-volunteers.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AppShell
      eyebrow="Admin"
      title="Volunteer roster"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowForm((open) => !open)}>
            <Plus className="mr-1.5 h-4 w-4" /> {showForm ? "Close" : "New volunteer"}
          </Button>
          <Button variant="outline" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" /> Export CSV
          </Button>
        </div>
      }
    >
      <p className="t-meta mb-4">
        Warrior Volunteers are the approved roster with accounts and outreach tools. People who only
        claimed a spot at an event are event helpers, listed under Events.
      </p>
      {showForm ? (

        <form onSubmit={handleCreate} className="panel mb-6 space-y-4 p-5">
          <p className="t-meta">
            Add a volunteer directly. This creates their account, gives them the volunteer role, and
            builds their profile.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="v-name">Full name</Label>
              <Input id="v-name" name="full_name" required maxLength={120} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-email">Email</Label>
              <Input id="v-email" name="email" type="email" required maxLength={255} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-phone">Phone</Label>
              <Input id="v-phone" name="phone" maxLength={40} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-city">City</Label>
              <Input id="v-city" name="city" placeholder="Burlington" maxLength={120} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-state">State</Label>
              <select
                id="v-state"
                name="state"
                defaultValue=""
                className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
              >
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value} disabled={state.value === ""}>
                    {state.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-track">Track</Label>
              <select
                id="v-track"
                name="volunteer_track"
                defaultValue="remote"
                className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm"
              >
                <option value="remote">Remote outreach</option>
                <option value="onsite">On-site crew</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="v-skills">Skills</Label>
              <Input id="v-skills" name="skills" placeholder="Comma separated" maxLength={300} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="v-t1d">T1D connection</Label>
            <Textarea id="v-t1d" name="t1d_connection" rows={2} maxLength={500} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="invite" defaultChecked className="h-4 w-4" />
            Send them an invite email to set a password
          </label>
          <Button type="submit" disabled={busy}>
            {busy ? "Creating..." : "Create volunteer"}
          </Button>
        </form>
      ) : null}

      <div className="panel overflow-x-auto">

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Volunteer</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Last event signed up</TableHead>
              <TableHead>Shirt size</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-24"></TableHead>

            </TableRow>
          </TableHeader>
          <TableBody>
            {volunteers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                  No volunteers yet. Approve an application to provision the first one.
                </TableCell>
              </TableRow>
            ) : null}
            {volunteers.map((volunteer) => (
              <TableRow key={volunteer.id}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => setOpenVolunteerId(volunteer.id)}
                    className="text-left font-semibold text-ice underline-offset-4 hover:underline"
                  >
                    {volunteer.full_name || "Unnamed"}
                  </button>
                  {volunteer.status === "pending" ? (
                    <span className="ml-2 inline-block rounded-sm border border-destructive/40 bg-destructive/10 px-1.5 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-destructive">
                      Pending
                    </span>
                  ) : null}
                </TableCell>

                <TableCell>
                  <p className="text-sm">{volunteer.email}</p>
                  <p className="t-meta">{volunteer.phone || "No phone"}</p>
                </TableCell>
                <TableCell>
                  {[volunteer.city, volunteer.state].filter(Boolean).join(", ") || "-"}
                </TableCell>
                <TableCell className="max-w-xs">
                  {volunteer.last_event ? (
                    <>
                      <p className="truncate text-sm">{volunteer.last_event.title}</p>
                      <p className="t-meta">
                        {volunteer.last_event.eventDate
                          ? new Date(
                              `${volunteer.last_event.eventDate}T12:00:00`,
                            ).toLocaleDateString()
                          : "Date TBD"}
                        {volunteer.last_event.slotTitle
                          ? ` . ${volunteer.last_event.slotTitle}`
                          : ""}
                      </p>
                    </>
                  ) : (
                    <span className="t-meta">No event signups</span>
                  )}
                </TableCell>
                <TableCell className="t-meta">{volunteer.shirt_size || "-"}</TableCell>

                <TableCell className="t-meta">
                  {new Date(volunteer.joined_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <select
                    value={volunteer.status}
                    onChange={async (event) => {
                      try {
                        await setStatus({ data: { id: volunteer.id, status: event.target.value } });
                        toast.success("Status updated.");
                        queryClient.invalidateQueries({ queryKey: ["admin", "volunteers"] });
                      } catch (error) {
                        reportHandledSync(error, { feature: "admin", action: "exportCsv" });
                        toast.error(
                          error instanceof Error ? error.message : "Could not update that status.",
                        );
                        queryClient.invalidateQueries({ queryKey: ["admin", "volunteers"] });
                      }
                    }}

                    className="h-9 rounded-sm border border-input bg-background px-2 text-xs text-ice"
                  >
                    {["pending", "active", "paused", "inactive"].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => setOpenVolunteerId(volunteer.id)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <VolunteerDetailSheet
        volunteerId={openVolunteerId}
        onOpenChange={(open) => {
          if (!open) setOpenVolunteerId(null);
        }}
      />
    </AppShell>
  );
}
